import { apiGetRequest, apiPostRequest, apiPutRequest } from "../../../Helpers/Requests";
import endpoints from "../../../Helpers/Endpoints";
import PointerUtils from "../../../Utils/pointer";

/**
 * Fetches the supplier's offered catalog items grouped by category for the
 * community-manager review. `vertical` picks the catalog: "products" (default,
 * the Products step) or "restaurants" (the Restaurants step).
 */
export const getSupplierProducts = async (supplierId, vertical = "products") => {
  return await apiGetRequest(
    endpoints.getSupplierProfileProducts(supplierId, vertical)
  );
};

/**
 * Accepts a supplier's catalog offer for the selected categories, in the given
 * vertical. Both verticals share the endpoint — see Admin_system verticals.ts.
 */
export const acceptProductOffer = async (
  supplierId,
  categoryIds,
  vertical = "products"
) => {
  try {
    PointerUtils.disable();
    const params = new URLSearchParams();
    categoryIds.forEach((id) => params.append("category_ids", id));
    if (vertical === "restaurants") params.append("vertical", "restaurants");
    const url = `${endpoints.acceptSupplierProductOffer(supplierId)}?${params}`;
    return await apiPostRequest(url);
  } finally {
    PointerUtils.enable();
  }
};

/**
 * Approves the supplier's whole offer in one transactional request. The modal
 * used to POST once per vertical, which could half-succeed and notified the
 * supplier once per call.
 */
export const acceptWholeOffer = async (
  supplierId,
  { services = [], products = [], restaurants = [] }
) => {
  try {
    PointerUtils.disable();
    return await apiPostRequest(endpoints.acceptSupplierOfferAll(supplierId), {
      services,
      products,
      restaurants,
    });
  } finally {
    PointerUtils.enable();
  }
};

export const inviteSupplier = async (supplierId) => {
  try {
    PointerUtils.disable();
    return await apiPostRequest(endpoints.inviteSupplier(supplierId));
  } finally {
    PointerUtils.enable();
  }
};

export const pauseSupplier = async (supplierId) => {
  try {
    PointerUtils.disable();
    return await apiPostRequest(endpoints.pauseSupplier(supplierId));
  } finally {
    PointerUtils.enable();
  }
};

export const activateSupplier = async (supplierId) => {
  try {
    PointerUtils.disable();
    return await apiPostRequest(endpoints.activateSupplier(supplierId));
  } finally {
    PointerUtils.enable();
  }
};

export const acceptOffer = async (supplierId, categoryIds) => {
  try {
    PointerUtils.disable();
    const params = new URLSearchParams();
    categoryIds.forEach((id) => params.append("category_ids", id));
    const url = `${endpoints.acceptSupplierOffer(supplierId)}?${params}`;
    return await apiPostRequest(url);
  } finally {
    PointerUtils.enable();
  }
};

export const updateSupplierContract = async (supplierId, categoryIds) => {
  try {
    PointerUtils.disable();
    const params = new URLSearchParams();
    categoryIds.forEach((id) => params.append("category_ids", id));
    const url = `${endpoints.updateExistingContract(supplierId)}?${params}`;
    return await apiPutRequest(url);
  } finally {
    PointerUtils.enable();
  }
};

/**
 * Orders still running for this supplier in the CM's community. Non-zero does
 * not block removal — it tells the remove modal to spell out what happens to
 * those orders (the supplier still completes them) and to offer the pause as
 * the softer alternative.
 */
export const getSupplierOpenOrders = async (supplierId) => {
  return await apiGetRequest(endpoints.supplierOpenOrders(supplierId));
};

export const removeSupplier = async (supplierId, reason) => {
  try {
    PointerUtils.disable();
    const url = `${endpoints.removeSupplierProfile(
      supplierId
    )}?reason=${encodeURIComponent(reason)}`;
    return await apiPostRequest(url);
  } finally {
    PointerUtils.enable();
  }
};

export const removeSupplierOffer = async (supplierId, reason) => {
  try {
    PointerUtils.disable();
    const url = `${endpoints.declineSupplierOffer(
      supplierId
    )}?reason=${encodeURIComponent(reason)}`;
    return await apiPostRequest(url);
  } finally {
    PointerUtils.enable();
  }
};
